'use client';

import { useTranslations } from 'next-intl';
import React, { FC, memo, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { Avatar, Gender } from '@/.gql/graphql';
import { useStyleContext, useGTMContext } from '@/src/providers';

const styles = {
  optionWrap: 'flex items-center',
  option: 'cursor-pointer flex items-center space-x-2 shrink-0 max-w-full',
  circle:
    'w-4 h-4 shrink-0 flex items-center justify-center rounded-full border-2 bg-transparent transition-all',
  circleActive: 'border-rose',
  circleInactive: 'border-gray-400',
  circleInner: 'block w-1.5 h-1.5 rounded-full bg-fill-rose',
  optionLabel: 'text-sm font-medium text-label-secondary',
  optionLabelActive: 'text-label-primary',
};

export interface TAvatarItemProps {
  avatar: Avatar;
  isHeader?: boolean;
}

const AvatarItemComponent: FC<TAvatarItemProps> = ({ avatar, isHeader }) => {
  const t = useTranslations('imageModal');
  const {
    activeAvatar,
    setActiveAvatar,
    setGenderMode,
    isYourPhoto,
    setIsYourPhoto,
  } = useStyleContext();
  const sendGTM = useGTMContext();

  const handleAvatarChange = (avatarId: string, avatarGender: Gender) => {
    setActiveAvatar(avatarId);
    setGenderMode(avatarGender);
    setIsYourPhoto(false);

    if (isHeader) {
      sendGTM({
        event: 'Gender_choose',
        gender: avatarGender,
      });
    }
  };

  const genderKeyMap: Record<Gender, string> = {
    [Gender.Female]: 'female',
    [Gender.Male]: 'male',
    [Gender.FemaleAndFemale]: 'femaleFemale',
    [Gender.MaleAndMale]: 'maleMale',
    [Gender.FemaleAndMale]: 'femaleMale',
    [Gender.MaleAndFemale]: 'maleFemale',
  };

  const genderLabel = t(genderKeyMap[avatar?.gender || Gender.Male]);

  const isActive = useMemo(
    () => activeAvatar === avatar?.id && !isYourPhoto,
    [activeAvatar, avatar?.id, isYourPhoto],
  );

  return (
    <div key={avatar?.id} className={styles.optionWrap}>
      <input
        type="radio"
        id={avatar?.id}
        name="radioGroup"
        value={avatar?.id}
        checked={isActive}
        onChange={() =>
          handleAvatarChange(avatar?.id || '', avatar?.gender || Gender.Male)
        }
        className="hidden"
      />
      <label htmlFor={avatar?.id} className={styles.option}>
        <span
          className={twMerge(
            styles.circle,
            isActive ? styles.circleActive : styles.circleInactive,
          )}
        >
          {isActive && <span className={styles.circleInner}></span>}
        </span>
        <span
          className={twMerge(
            styles.optionLabel,
            isActive && styles.optionLabelActive,
          )}
        >
          {avatar?.name} {genderLabel}
        </span>
      </label>
    </div>
  );
};

export const AvatarItem = memo(AvatarItemComponent);
